import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Tabs, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import EditFieldPanel from "../ConfigurePanel/EditFieldPanel";
import FilterDrawer from "../../layout/Workspace/drawers/FilterDrawer";
import {
  setChartEditField,
  setX_attribute,
  setY_attribute,
} from "../../../redux/actions/WidgetMaker/GraphConfig";
import { setWidgetMakerApiCallFlag } from "../../../redux/actions/WidgetMaker/WidgetMaker";
import {
  setFilterActiveKey,
  setisFilterPanelVisible,
} from "../../../redux/actions/Filter";
import "./EditFieldModal.css";

// Custom hook for scroll reset functionality
const useScrollReset = (filterContainerRef) => {
  return useCallback(() => {
    requestAnimationFrame(() => {
      // Reset filter container
      if (filterContainerRef.current) {
        filterContainerRef.current.scrollTop = 0;
      }

      // Reset common scrollable elements for filters tab
      const selectors = [
        ".filter-drawer-container",
        ".filters-list-container",
        ".ant-collapse-content-box",
        ".filters-tab-content",
        ".ant-tabs-tabpane-active",
      ];

      selectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element?.scrollTop !== undefined) {
          element.scrollTop = 0;
        }
      });
    });
  }, [filterContainerRef]);
};

const EditFieldModal = ({
  isModalVisible,
  setIsModalVisible,
  editName,
  setEditName,
  editAggregation,
  setAggregation,
  filterSearch,
  setfilterSearch,
  isFilterApplied,
  setisFilterApplied,
  widgetMakerClearFilter,
  setWidgetMakerClearFilter,
  searchTerm,
  setSearchTerm,
  callChartAPI,
  onChange,
}) => {
  const dispatch = useDispatch();
  const axisPanelAtt = useSelector(
    (state) => state.GraphConfig.widgetGraphConfig
  );
  const wm_api_flag = useSelector((state) => state.WidgetMaker.wm_api_flag);
  const All_WM_attributes = useSelector(
    (state) => state.WidgetMaker.All_WM_attributes
  );
  const widgetGraphConfig = useSelector(
    (state) => state?.GraphConfig?.widgetGraphConfig
  );
  const radioArray = useSelector((state) => state.Filter.radioArray);
  const reduxRadioChecked = useSelector((state) => state.Filter.radioChecked);
  const authParams = useSelector((state) => state.Authentication.authParams);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const editFieldApplyRef = useRef(null);
  const [editXlimit, setEditXlimit] = useState(null);
  const filterDrawerRef = useRef(null);
  const [radioChecked, setRadioChecked] = useState({});
  const [filterUpdated, setFilterUpdated] = useState(false);
  const [isFilterAppliedOnYAxis, setisFilterAppliedOnYAxis] = useState(false);
  const currentSeriesType = axisPanelAtt?.edit_fields?.series;
  // Use custom hook for scroll reset
  const resetScrollPosition = useScrollReset(filterDrawerRef);

  // Custom setRadioChecked wrapper to ensure proper state updates
  const handleRadioCheckedUpdate = useCallback(
    (newRadioChecked) => {
      setRadioChecked(newRadioChecked);

      // Also update filterUpdated state immediately when radio buttons change
      if (currentSeriesType) {
        const currentRadioChecked =
          newRadioChecked?.[currentSeriesType]?.data || {};
        const currentRadioArray = radioArray?.[currentSeriesType]?.data || [];

        const hasRadioSelections = Object.keys(currentRadioChecked)?.length > 0;
        const hasArraySelections =
          Array.isArray(currentRadioArray) && currentRadioArray.length > 0;

        setFilterUpdated(hasRadioSelections || hasArraySelections);
      }
    },
    [currentSeriesType, radioArray]
  );

  // Sync local radioChecked state with Redux store
  useEffect(() => {
    if (reduxRadioChecked) {
      setRadioChecked(reduxRadioChecked);
    }
  }, [reduxRadioChecked]);

  // Set filter panel visibility when modal opens
  useEffect(() => {
    if (isModalVisible && axisPanelAtt?.edit_fields?.boolValue) {
      dispatch(setisFilterPanelVisible(true));
      // Reset to Edit Field tab when modal opens
      setActiveTabKey("1");

      // Reset scroll position when modal opens
      setTimeout(resetScrollPosition, 100);
    }
    return () => {
      // Clean up when modal closes
      dispatch(setisFilterPanelVisible(false));
    };
  }, [
    isModalVisible,
    axisPanelAtt?.edit_fields?.boolValue,
    dispatch,
    resetScrollPosition,
  ]);

  // Additional effect to handle Redux state changes after modal is open
  useEffect(() => {
    if (
      isModalVisible &&
      currentSeriesType &&
      axisPanelAtt?.edit_fields?.boolValue
    ) {
      // Recalculate filter states when Redux state changes
      const currentRadioChecked =
        reduxRadioChecked?.[currentSeriesType]?.data || {};
      const currentRadioArray = radioArray?.[currentSeriesType]?.data || [];
      const filterArray =
        axisPanelAtt.y_axis?.[currentSeriesType]?.filters || [];

      const hasRadioSelections = Object.keys(currentRadioChecked)?.length > 0;
      const hasArraySelections =
        Array.isArray(currentRadioArray) && currentRadioArray.length > 0;
      const hasAppliedFilters =
        Array.isArray(filterArray) && filterArray.length > 0;

      setFilterUpdated(hasRadioSelections || hasArraySelections);
      setisFilterAppliedOnYAxis(hasAppliedFilters);
    }
  }, [
    isModalVisible,
    currentSeriesType,
    reduxRadioChecked,
    radioArray,
    axisPanelAtt?.y_axis,
    axisPanelAtt?.edit_fields?.boolValue,
  ]);

  // Check if any radio buttons have been changed for the current series
  useEffect(() => {
    if (axisPanelAtt?.y_axis) {
      let filterArray = axisPanelAtt.y_axis[currentSeriesType]?.filters;
      setisFilterAppliedOnYAxis(
        Array.isArray(filterArray) && filterArray.length > 0
      );
    }
  }, [axisPanelAtt?.y_axis, currentSeriesType]);
  useEffect(() => {
    // if (!currentSeriesType) return false;
    if (currentSeriesType) {
      // Check both radioChecked and radioArray for filter selections
      const currentRadioChecked = radioChecked?.[currentSeriesType]?.data || {};
      const currentRadioArray = radioArray?.[currentSeriesType]?.data || [];

      const hasRadioSelections = Object.keys(currentRadioChecked)?.length > 0;
      const hasArraySelections =
        Array.isArray(currentRadioArray) && currentRadioArray.length > 0;

      setFilterUpdated(hasRadioSelections || hasArraySelections);
    }
  }, [
    radioChecked,
    radioArray,
    currentSeriesType,
    radioChecked?.[currentSeriesType]?.data,
    radioArray?.[currentSeriesType]?.data,
  ]);

  // Reset scroll position when switching to filters tab
  useEffect(() => {
    if (activeTabKey === "2" && isModalVisible) {
      setTimeout(resetScrollPosition, 150);
    }
  }, [activeTabKey, isModalVisible, resetScrollPosition]);

  // Monitor widgetMakerClearFilter changes to reset filter states
  useEffect(() => {
    if (widgetMakerClearFilter) {
      // Reset filter states when clear filter is triggered
      setFilterUpdated(false);
      setisFilterAppliedOnYAxis(false);
      setRadioChecked({});
    }
  }, [widgetMakerClearFilter]);

  const handleModalClose = () => {
    setIsModalVisible(false);
    // Reset edit fields state
    dispatch(setChartEditField({ boolValue: false }, axisPanelAtt));
    // Reset active tab to Edit Field
    setActiveTabKey("1");
    // Reset filter search
    if (setfilterSearch) {
      setfilterSearch("");
    }
    // Reset search term
    if (setSearchTerm) {
      setSearchTerm("");
    }
    resetScrollPosition();
    dispatch(setFilterActiveKey([]));
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);

    // Handle any specific logic when switching tabs
    if (key === "2") {
      // Switching to Filters tab
      // Ensure filter panel is visible
      dispatch(setisFilterPanelVisible(true));
      setTimeout(resetScrollPosition, 100);
    } else if (key === "1") {
      // Switching to Edit Field tab
      // Any specific logic for edit field tab
    }

    // Call the onChange prop if provided
    if (onChange) {
      onChange(key);
    }
  };

  const handleApply = () => {
    // Apply based on active tab
    if (activeTabKey === "2") {
      // Filters tab
      // For KPI cards and y-series, we need to maintain the aggregation when applying filters
      if (
        (axisPanelAtt?.edit_fields?.series?.includes("kpi") ||
          axisPanelAtt?.edit_fields?.series?.includes("y_series")) &&
        editFieldApplyRef.current
      ) {
        // Call the edit field apply function to ensure aggregation is preserved
        // editFieldApplyRef.current();
        // setisFilterApplied(true);
        setTimeout(() => {
          setIsModalVisible(false);
        }, 100);

        dispatch(setFilterActiveKey([]));
        setisFilterApplied(true);
      } else {
        setisFilterApplied(true);
        setIsModalVisible(false);
        dispatch(setChartEditField({ boolValue: false }, axisPanelAtt));
      }
    } else if (activeTabKey === "1") {
      // Edit Field tab
      // Check if editName is empty
      if (!editName || editName === "") {
        return;
      }

      // Call the stored apply function from EditFieldPanel
      if (editFieldApplyRef.current) {
        editFieldApplyRef.current();
        // Close modal after a short delay to ensure state updates
        setTimeout(() => {
          setIsModalVisible(false);
        }, 100);
      } else {
        // If no apply function is available, close modal
        setIsModalVisible(false);
      }
    }
  };

  // Get the attribute display name
  const getAttributeDisplayName = () => {
    const attName = axisPanelAtt?.edit_fields?.attName;
    if (!All_WM_attributes || typeof All_WM_attributes !== "object") {
      return attName || "Edit Field";
    }
    return All_WM_attributes[attName] || attName || "Edit Field";
  };

  // Check if Filters tab should be shown
  const showFiltersTab =
    axisPanelAtt?.chart_type !== "cluster-chart" &&
    !(
      (
        (axisPanelAtt?.chart_type === "grid" ||
          axisPanelAtt?.chart_type === "pivot-grid") &&
        axisPanelAtt?.edit_fields?.series?.includes("y_series")
      ) // This is for Grid or Pivot
    ) &&
    !(
      (
        axisPanelAtt?.edit_fields?.series === "x_axis" ||
        axisPanelAtt?.edit_fields?.series === "breakdown" ||
        axisPanelAtt?.edit_fields?.series === "group_by" ||
        axisPanelAtt?.edit_fields?.series === "filter_dropdown"
      ) // Exclude group_by from filters tab
    ) &&
    axisPanelAtt?.edit_fields?.data_source !== "page" &&
    // axisPanelAtt?.edit_fields?.series?.includes("y_series") &&
    (axisPanelAtt?.edit_fields?.series?.split("_")[0] === "kpi" ||
      axisPanelAtt?.edit_fields?.series?.includes("y_series") ||
      [
        "combination",
        "bar",
        "lollipop",
        "horizontal-bar",
        "area",
        "line",
        "kpi",
        "progress-kpi",
        "scatter",
        "quadrant",
        "grid",
        "pivot-grid",
      ]?.includes(axisPanelAtt?.chart_type));

  // Dynamic height based on active tab
  const getModalBodyHeight = () => {
    if (activeTabKey === "1") {
      // Edit Field tab - adjust based on content
      return "auto";
    } else if (activeTabKey === "2") {
      // Filters tab - fixed height for scrollable content
      return "600px";
    }
    return "auto";
  };

  return (
    <Modal
      title={
        <>
          <div className="edit-field-modal-title-left">
            Edit {getAttributeDisplayName()}
          </div>
          <div className="edit-field-modal-subtitle">
            Configure your field settings{showFiltersTab ? " and filters" : ""}
          </div>
        </>
      }
      visible={isModalVisible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      centered
      className={`edit-field-modal ${
        activeTabKey === "1" ? "edit-field-active" : "filters-active"
      } ${authParams?.theme_type === "1" ? "dark-theme" : ""}`}
      bodyStyle={{ height: getModalBodyHeight(), overflow: "hidden" }}
      transitionName=""
      maskTransitionName=""
    >
      <Tabs
        defaultActiveKey="1"
        activeKey={activeTabKey}
        onChange={handleTabChange}
        destroyInactiveTabPane={false}
        animated={false}
        style={{
          height: "100%",
        }}
      >
        <Tabs.TabPane tab="Edit Field" key="1">
          <div className="edit-field-tab-content edit-field-content">
            <EditFieldPanel
              editName={editName}
              setEditName={setEditName}
              editAggregation={editAggregation}
              setAggregation={setAggregation}
              setEditPanel={axisPanelAtt?.edit_fields}
              editPanel={axisPanelAtt?.edit_fields}
              yData={axisPanelAtt?.y_axis}
              editseries={axisPanelAtt?.edit_fields?.series}
              onApply={(fn) => {
                editFieldApplyRef.current = fn;
              }}
              onEditXlimitChange={(value) => {
                setEditXlimit(value);
              }}
            />
          </div>
          {/* Edit Field tab buttons */}
          <div className="edit-field-button-container">
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleApply}
              disabled={
                !editName ||
                editName === "" ||
                editXlimit === "" ||
                editXlimit === null ||
                editXlimit === undefined ||
                editXlimit <= 0
              }
            >
              Apply
            </Button>
          </div>
        </Tabs.TabPane>
        {showFiltersTab && (
          <Tabs.TabPane tab="Filters" key="2">
            <div className="edit-field-tab-content filters-tab-content">
              <div className="filter-drawer-container" ref={filterDrawerRef}>
                <div className="filters-list-container">
                  <FilterDrawer
                    drawer={false}
                    type={axisPanelAtt?.edit_fields?.series}
                    filterSearch={filterSearch}
                    setfilterSearch={setfilterSearch}
                    isFilterApplied={isFilterApplied}
                    setisFilterApplied={setisFilterApplied}
                    widgetMakerClearFilter={widgetMakerClearFilter}
                    setWidgetMakerClearFilter={setWidgetMakerClearFilter}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    editAggregation={editAggregation}
                    editName={editName}
                    setRadioChecked={handleRadioCheckedUpdate}
                  />
                </div>
              </div>
            </div>

            {/* Filters tab buttons */}
            <div className="edit-field-button-container">
              <Button onClick={handleModalClose}>Cancel</Button>
              <div className="edit-field-filters-buttons">
                <Button
                  // className="ant-btn-clear-filters"
                  className={
                    !isFilterAppliedOnYAxis && !filterUpdated ? "disable" : ""
                  }
                  // disabled={!isFilterAppliedOnYAxis && !filterUpdated}
                  onClick={() => {
                    setWidgetMakerClearFilter(!widgetMakerClearFilter);
                    // Reset local filter states when clearing
                    setFilterUpdated(false);
                    setRadioChecked({});
                  }}
                  style={{ marginRight: "8px" }}
                >
                  Clear Filters
                </Button>
                <Button
                  type="primary"
                  onClick={handleApply}
                  // className={
                  //   !isFilterAppliedOnYAxis && !filterUpdated ? "disable" : ""
                  // }
                  // disabled={!isFilterAppliedOnYAxis && !filterUpdated}
                >
                  Apply
                </Button>
              </div>
            </div>
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default EditFieldModal;
